import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { formatDate, formatDateTime } from '../../../lib/format';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

interface CustomFieldsCardProps {
  fields: [string, unknown][];
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})/;
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const URGENCY_COLORS: Record<string, 'red' | 'amber' | 'green' | 'neutral'> = {
  immediate: 'red',
  urgent: 'red',
  high: 'red',
  medium: 'amber',
  normal: 'amber',
  low: 'green',
  none: 'neutral',
};

const SENTIMENT_COLORS: Record<string, 'green' | 'amber' | 'red' | 'neutral'> = {
  very_positive: 'green',
  positive: 'green',
  neutral: 'amber',
  negative: 'red',
  very_negative: 'red',
};

function humanizeKey(key: string): string {
  return key
    .replaceAll(/([A-Z])/g, ' $1')
    .replaceAll(/[_-]/g, ' ')
    .replaceAll(/^./g, (s) => s.toUpperCase())
    .trim();
}

function isIsoDate(value: string): boolean {
  return ISO_DATE_REGEX.test(value) || ISO_DATE_ONLY_REGEX.test(value);
}

function formatValue(value: string): string {
  if (ISO_DATE_ONLY_REGEX.test(value)) return formatDate(value);
  if (ISO_DATE_REGEX.test(value)) return formatDateTime(value);
  return value;
}

function getBadgeColor(
  key: string,
  value: string,
): 'red' | 'amber' | 'green' | 'neutral' | 'cyan' | 'purple' | 'blue' {
  const lower = value.toLowerCase().replaceAll(/[_-]/g, '_');

  if (key.toLowerCase().includes('urgency') || key.toLowerCase().includes('priority')) {
    return URGENCY_COLORS[lower] ?? 'neutral';
  }
  if (key.toLowerCase().includes('sentiment') || key.toLowerCase().includes('overall')) {
    return SENTIMENT_COLORS[lower] ?? 'neutral';
  }
  if (key.toLowerCase().includes('channel') || key.toLowerCase().includes('type')) {
    return 'blue';
  }
  return 'cyan';
}

function shouldRenderAsBadge(key: string, value: string): boolean {
  const lower = value.toLowerCase();
  const keyLower = key.toLowerCase();
  const badgeKeys = [
    'urgency',
    'priority',
    'sentiment',
    'overall',
    'channel',
    'type',
    'level',
    'status',
    'engagement',
  ];
  return badgeKeys.some((k) => keyLower.includes(k)) && lower.length < 30;
}

function isScoreValue(key: string, value: number): boolean {
  const keyLower = key.toLowerCase();
  return (
    (keyLower.includes('score') || keyLower.includes('confidence')) && value >= 0 && value <= 1
  );
}

function getBarColor(percent: number): string {
  if (percent >= 70) return colors.status.success.main;
  if (percent >= 40) return colors.status.warning.main;
  return colors.status.error.main;
}

function ScoreBar({ value, label }: Readonly<{ value: number; label: string }>) {
  const percent = Math.round(value * 100);
  const barColor = getBarColor(percent);

  return (
    <VStack spacing="xs">
      <HStack justify="between" align="center">
        <Text variant="caption" color={colors.text.tertiary}>
          {label}
        </Text>
        <Text variant="caption" color={barColor} style={{ fontWeight: '600' }}>
          {percent}%
        </Text>
      </HStack>
      <Box
        backgroundColor={colors.background.secondary}
        borderRadius="full"
        style={{ height: 4, width: '100%' }}
      >
        <Box
          backgroundColor={barColor}
          borderRadius="full"
          style={{ height: 4, width: `${percent}%` }}
        />
      </Box>
    </VStack>
  );
}

function PrimitiveValue({
  fieldKey,
  value,
}: Readonly<{
  fieldKey: string;
  value: string | number | boolean;
}>) {
  if (typeof value === 'boolean') {
    return (
      <Badge size="sm" color={value ? 'green' : 'neutral'}>
        {value ? 'Yes' : 'No'}
      </Badge>
    );
  }

  if (typeof value === 'number') {
    if (isScoreValue(fieldKey, value)) {
      return <ScoreBar value={value} label={humanizeKey(fieldKey)} />;
    }
    return (
      <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '500' }}>
        {value.toLocaleString()}
      </Text>
    );
  }

  if (isIsoDate(value)) {
    return (
      <Text variant="bodySmall" color={colors.text.primary} style={{ fontWeight: '500' }}>
        {formatValue(value)}
      </Text>
    );
  }

  if (shouldRenderAsBadge(fieldKey, value)) {
    const displayValue = value.replaceAll(/[_-]/g, ' ').replaceAll(/^./g, (s) => s.toUpperCase());
    return (
      <Badge size="sm" color={getBadgeColor(fieldKey, value)}>
        {displayValue}
      </Badge>
    );
  }

  return (
    <Text
      variant="bodySmall"
      color={colors.text.primary}
      style={{ fontWeight: '500', flex: 1, textAlign: 'right' }}
    >
      {String(value)}
    </Text>
  );
}

function ArrayValue({ items }: Readonly<{ items: unknown[] }>) {
  const stringItems = items.filter((item): item is string => typeof item === 'string');

  if (stringItems.length === items.length && stringItems.every((s) => s.length < 40)) {
    return (
      <HStack spacing="xs" style={{ flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
        {stringItems.map((item) => (
          <Badge key={item} size="sm" color="cyan">
            {item}
          </Badge>
        ))}
      </HStack>
    );
  }

  return (
    <VStack spacing="sm" style={{ flex: 1 }}>
      {items.map((item, i) => {
        const key = `item-${i}-${String(item).slice(0, 20)}`;
        if (typeof item === 'object' && item !== null) {
          return <ObjectValue key={key} obj={item as Record<string, unknown>} depth={1} />;
        }
        return (
          <Text key={key} variant="bodySmall" color={colors.text.primary}>
            {String(item)}
          </Text>
        );
      })}
    </VStack>
  );
}

function ObjectValue({
  obj,
  depth = 0,
}: Readonly<{ obj: Record<string, unknown>; depth?: number }>) {
  const entries = Object.entries(obj).filter(([, v]) => v != null && v !== '');

  return (
    <Box
      // Depth is indent. The hairline rule that used to mark it was one more
      // edge on a screen that already had too many.
      style={
        depth > 0 ? { paddingLeft: tokens.spacing.md, marginTop: tokens.spacing.xs } : undefined
      }
    >
      <VStack spacing="sm">
        {entries.map(([key, value]) => (
          <FieldRow key={key} fieldKey={key} value={value} depth={depth} />
        ))}
      </VStack>
    </Box>
  );
}

function FieldRow({
  fieldKey,
  value,
  depth = 0,
}: Readonly<{
  fieldKey: string;
  value: unknown;
  depth?: number;
}>) {
  const label = humanizeKey(fieldKey);

  if (typeof value === 'number' && isScoreValue(fieldKey, value)) {
    return (
      <Box style={{ paddingVertical: tokens.spacing.xxs }}>
        <ScoreBar value={value} label={label} />
      </Box>
    );
  }

  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return (
      <HStack justify="between" align="center" spacing="md">
        <Text variant="caption" color={colors.text.tertiary} style={{ minWidth: 80 }}>
          {label}
        </Text>
        <PrimitiveValue fieldKey={fieldKey} value={value} />
      </HStack>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return (
      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.tertiary}>
          {label}
        </Text>
        <ArrayValue items={value} />
      </VStack>
    );
  }

  if (typeof value === 'object' && value !== null) {
    if (depth >= 2) {
      return (
        <HStack justify="between" align="center" spacing="md">
          <Text variant="caption" color={colors.text.tertiary} style={{ minWidth: 80 }}>
            {label}
          </Text>
          <Text
            variant="caption"
            color={colors.text.secondary}
            style={{ flex: 1, textAlign: 'right' }}
            numberOfLines={2}
          >
            {Object.entries(value as Record<string, unknown>)
              .filter(([, v]) => v != null)
              .map(([k, v]) => `${humanizeKey(k)}: ${String(v)}`)
              .join(', ')}
          </Text>
        </HStack>
      );
    }
    return (
      <VStack spacing="xs">
        <Text variant="caption" color={colors.text.tertiary}>
          {label}
        </Text>
        <ObjectValue obj={value as Record<string, unknown>} depth={depth + 1} />
      </VStack>
    );
  }

  return null;
}

function Divider() {
  return (
    <Box
      height={1}
      backgroundColor={colors.border.muted}
      style={{ marginVertical: tokens.spacing.xs }}
    />
  );
}

export function CustomFieldsCard({ fields }: Readonly<CustomFieldsCardProps>) {
  const { t } = useTranslation();

  if (fields.length === 0) return null;

  return (
    <VStack spacing="md">
      <Text variant="h6" color={colors.text.primary} style={{ fontWeight: '600' }}>
        {t('leads.detail.customFields')}
      </Text>
      {fields.map(([key, value], index) => (
        <Box key={key}>
          <FieldRow fieldKey={key} value={value} depth={0} />
          {index < fields.length - 1 && <Divider />}
        </Box>
      ))}
    </VStack>
  );
}
